/**
 * Window title = document context (CH-03).
 *
 * Native apps put the *document* in the title bar, because that's what the
 * window manager, Mission Control, Cmd+Tab and the Windows taskbar preview
 * all read — not which pricing tier is active. The tier is still visible in
 * `PersonalModeFooter` (`src/core/licensing/components/PersonalModeFooter.tsx`,
 * mounted from `MainShell.tsx`) for Personal/Trial/Grace; Pro deliberately
 * shows no persistent badge anywhere (there's nothing to upsell), so this was
 * never a loss of information for any state that needs reminding.
 *
 * Moved out of `src/core/licensing/hooks/` (its old home): the title no
 * longer depends on license state at all, so leaving it under `licensing/`
 * would be a stale filing. `src/layout/` already hosts other renderer hooks
 * that bridge workspace state to the Electron shell (see
 * `src/layout/menu/useAppMenuCommands.ts`), so this is the natural new home.
 */

import { useEffect, useRef } from 'react';
import { useWorkspaceStore, isQueryTab, isNotebookTab, isExecutionPlanTab } from '../../features/workspace';
import type { Tab } from '../../features/workspace';
import { getFileNameFromPath } from '../../features/workspace/query-editor/utils/queryFileUtils';
import { useDataImportStore } from '../../features/workspace/data-import/store/dataImportStore';

export const APP_NAME = 'Jam SQL Studio';

/**
 * Pure title builder. `undefined`/`null`/blank means "no document" (the home
 * screen) — plain app name. Otherwise `<tab title> — Jam SQL Studio`, using a
 * literal em dash (U+2014) with spaces, the macOS/Windows convention. Does
 * not attempt to detect or escape an em dash already inside the tab title —
 * concatenation never mangles it.
 */
export function buildWindowTitle(activeTabTitle: string | null | undefined): string {
  const trimmed = activeTabTitle?.trim();
  return trimmed ? `${trimmed} — ${APP_NAME}` : APP_NAME;
}

/**
 * The title fragment for the active tab, or `undefined` for "no document".
 *
 * Mirrors the same "file name wins over the stored tab title" rule
 * `Tabs.tsx` uses for the tab-strip label (its `baseTitle`) — a file-backed
 * tab should read the same in the title bar as it does in its own tab.
 *
 * The start-page tab (id `tab-start`, title "Home") counts as "no active
 * tab": the home screen isn't a document, so it collapses to the plain app
 * name via `buildWindowTitle`.
 */
export function getActiveTabWindowTitle(tab: Tab | null | undefined): string | undefined {
  if (!tab || tab.type === 'start-page') return undefined;
  const filePath = getActiveTabFilePath(tab);
  if (!filePath) return tab.title;
  return getFileNameFromPath(filePath) || tab.title;
}

/**
 * Absolute file path backing the active tab, or `undefined` when the tab
 * isn't genuinely file-backed. Feeds macOS `setRepresentedFilename`; never
 * invent a path for a tab that has none, and never hand it a value the app
 * itself can't vouch for (a broken proxy icon is worse than none).
 *
 * Checked (2026-07-31, round 1 fix): every `filePath`-shaped field across
 * `src/features/workspace/**` type files, not just `workspaceTypes.ts` — a
 * grep scoped to one file previously produced a false "no other tab kind has
 * one" claim that missed fields declared elsewhere. Per kind:
 * - `QueryTabContent.filePath` / `NotebookTabContent.filePath` — INCLUDED.
 *   Only ever set from a native Save/Open dialog or a real OS drag-drop.
 * - `ExecutionPlanTabContent.filePath` — INCLUDED. Same native-dialog/
 *   drag-drop provenance (`importExecutionPlan`, `getDroppedFilePath` in
 *   `ExecutionPlanTab.tsx`). `baselineFilePath`/`compareFilePath` (the two
 *   *other* plans loaded alongside it in compare view) are deliberately
 *   NOT used — `content.filePath` stays the primary/current plan across both
 *   `view: 'single'` and `view: 'compare'` (see `handleToggleCompare` in
 *   `ExecutionPlanTab.tsx`), so there is still exactly one file this tab
 *   "is"; the baseline/compare paths are reference material bolted on, not
 *   the document itself.
 * - `DataImportTabContent.filePath` — EXCLUDED. Unlike the three above, this
 *   one is NOT exclusively dialog/drop-sourced: `SourceStep.tsx`'s
 *   `handlePathChange` lets the user free-type a path directly into
 *   `configActions.setFilePath`, and the wizard tracks its own `fileMissing`
 *   flag (`analysisStatus.kind === 'error'`) precisely because the app does
 *   not trust this field to resolve. That validation state lives in the
 *   ephemeral per-tab `dataImportStore`, not in `DataImportTabContent`
 *   itself (by design — see the type's own doc comment), so this pure,
 *   content-only function has no way to tell a good path from a bad one.
 *   Representing an unverified path is exactly the failure mode the CH-03
 *   brief warns against, so this tab kind stays out until/unless a validated
 *   signal is threaded through.
 * - `SchemaCompareEndpoint.folderPath` (reachable from
 *   `SchemaCompareTabContent`) — EXCLUDED. It is a *folder* (a Database
 *   Blueprint mount), not a document file, and a schema-compare tab has TWO
 *   endpoints (source/target) that can each be folder-backed — there is no
 *   single file this tab "is".
 * - `DatabaseBlueprintTabContent.selectedFilePath` — EXCLUDED. Transient
 *   peek-viewer selection inside a folder-backed tab (`blueprintFolderId` is
 *   what the tab actually represents), not the tab's own document; it can be
 *   `undefined` moment to moment as the user browses.
 * - `ChartExportRequest.filePath` / `ChartExportResult.filePath`
 *   (`chartTypes.ts`) — not a tab-content field at all (a chart PNG/SVG
 *   export request/result), unreachable via any `tab.content`.
 */
export function getActiveTabFilePath(tab: Tab | null | undefined): string | undefined {
  if (!tab) return undefined;
  const filePath = isQueryTab(tab)
    ? tab.content.filePath
    : isNotebookTab(tab)
      ? tab.content.filePath
      : isExecutionPlanTab(tab)
        ? tab.content.filePath
        : undefined;
  return filePath && filePath.trim() ? filePath : undefined;
}

/**
 * Mirrors the exact "would closing this tab warn the user" guards
 * `Tabs.tsx`'s `handleCloseTab` already consults (T2.1 routed Cmd/Ctrl+W
 * through `WORKSPACE_CLOSE_ACTIVE_TAB_EVENT` into that same function) — the
 * one existing notion of "dirty" this feeds off, per that guard's own logic:
 * an active/errored transaction on a query tab, an unsaved notebook, or a
 * live data-import run. Deliberately NOT the separate `*`-prefix
 * file-out-of-date signal shown in the tab strip (`isFileOutOfDate`) — that
 * tracks unsaved *file* edits, a different question from "would closing this
 * tab prompt a dialog". Feeds macOS `setDocumentEdited`.
 */
export function isActiveTabDocumentEdited(
  tab: Tab | null | undefined,
  hasActiveImportRun: boolean
): boolean {
  if (!tab) return false;
  if (isQueryTab(tab)) {
    return tab.content.transactionState === 'active' || tab.content.transactionState === 'error';
  }
  if (isNotebookTab(tab)) {
    return Boolean(tab.content.isDirty);
  }
  if (tab.type === 'data-import') {
    return hasActiveImportRun;
  }
  return false;
}

/**
 * Sets `document.title` (which Electron mirrors to the native window title)
 * from the active workspace tab, and — on macOS only — pushes the represented
 * file + edited-dot context over IPC. Both IPC values are ref-compared so a
 * render that doesn't change either piece never re-invokes the bridge.
 */
export function useWindowTitle(): void {
  const activeTabId = useWorkspaceStore((s) => s.activeTabId);
  const activeTab = useWorkspaceStore((s) => s.tabs.find((t) => t.id === s.activeTabId));
  const activeRuns = useDataImportStore((s) => s.activeRuns);

  const windowTitle = buildWindowTitle(getActiveTabWindowTitle(activeTab));
  const filePath = getActiveTabFilePath(activeTab) ?? null;
  const hasActiveImportRun = Boolean(activeTabId && activeRuns[activeTabId]);
  const isEdited = isActiveTabDocumentEdited(activeTab, hasActiveImportRun);

  useEffect(() => {
    document.title = windowTitle;
  }, [windowTitle]);

  const lastContextRef = useRef<{ filePath: string | null; isEdited: boolean } | null>(null);

  useEffect(() => {
    // Skip the IPC round trip entirely on non-macOS rather than relying on
    // the main process to no-op — `windowChrome.platform` is already
    // published synchronously, no extra lookup needed.
    if (window.electronAPI?.windowChrome?.platform !== 'darwin') return;

    const last = lastContextRef.current;
    if (last && last.filePath === filePath && last.isEdited === isEdited) return;
    lastContextRef.current = { filePath, isEdited };

    window.electronAPI?.windowChrome
      ?.setDocumentContext?.({ filePath, isEdited })
      .catch(() => {});
  }, [filePath, isEdited]);
}
